import { useQuery } from "react-query";
import { zodParseWithDetailedLog } from "src/api/apiErrorLogs";
import { loggedInApiCall } from "src/api/axiosClient/userApi";
import { z } from "zod";
import { getConsentHeaders } from "./consentHeaders";

export const getContractualAcceptancesKey = "getContractualAcceptances" as const;

const ContractualAcceptanceCategorySchema = z.string().nullable().optional();

const ContractualAcceptanceSchema = z
    .object({
        recordId: z.number(),
        itemId: z.number(),
        versionId: z.number(),
        code: z.string(),
        name: z.string(),
        category: ContractualAcceptanceCategorySchema,
        documentCategory: ContractualAcceptanceCategorySchema,
        itemCategory: ContractualAcceptanceCategorySchema,
        consentItemCategory: ContractualAcceptanceCategorySchema,
        consentItem: z
            .object({
                category: ContractualAcceptanceCategorySchema,
            })
            .nullable()
            .optional(),
        version: z.string(),
        title: z.string(),
        summary: z.string().nullable().optional(),
        content: z.string().nullable().optional(),
        documentUrl: z.string().nullable().optional(),
        acceptedAt: z.string(),
    })
    .transform(({ category, consentItem, consentItemCategory, documentCategory, itemCategory, ...acceptance }) => ({
        ...acceptance,
        category: category ?? itemCategory ?? documentCategory ?? consentItemCategory ?? consentItem?.category ?? null,
    }));

export type ContractualAcceptance = z.infer<typeof ContractualAcceptanceSchema>;

type Options = {
    enabled?: boolean;
};

export const useGetContractualAcceptances = (options?: Options) => {
    const { enabled = true } = options ?? {};

    const query = useQuery({
        queryKey: [getContractualAcceptancesKey],
        enabled,
        queryFn: async () => {
            const response = await loggedInApiCall.get("/api/users/me/contractual-acceptances", {
                headers: getConsentHeaders(),
            });

            return response;
        },
        select: (response) => {
            const acceptances = response.data["hydra:member"] ?? [];

            return zodParseWithDetailedLog(acceptances, ContractualAcceptanceSchema.array(), {
                operation: "GET",
                endpoint: "/users/me/contractual-acceptances",
            });
        },
    });

    return {
        contractualAcceptances: query.data,
        ...query,
    };
};
